<template>
    <div class="mb-6 flex flex-col gap-3">
        <div
            v-for="card in cards"
            :key="card.id"
            class="rounded-2xl border border-purple-200 bg-purple-50 dark:bg-purple-950/40 p-4 sm:p-5"
        >
            <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div class="flex items-start gap-3">
                    <!-- Partner avatar (initial circle, no photo lookup) -->
                    <div
                        class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-purple-200 dark:bg-purple-950/40 text-base font-bold text-purple-700 dark:text-purple-300"
                        aria-hidden="true"
                    >
                        {{ card.partnerInitial }}
                    </div>
                    <div class="flex-1">
                        <NuxtLink
                            :to="card.to"
                            class="font-semibold text-purple-700 dark:text-purple-300 underline decoration-purple-300 underline-offset-2 hover:text-purple-800 hover:dark:text-purple-300 hover:decoration-purple-500"
                        >
                            {{ card.title }}
                        </NuxtLink>
                        <p class="mt-0.5 text-sm text-gray-600 dark:text-txt-body">{{ card.subtitle }}</p>
                    </div>
                </div>
                <div class="flex shrink-0 flex-col items-stretch gap-1 self-start sm:items-end sm:self-auto">
                    <button
                        type="button"
                        class="inline-flex items-center justify-center gap-1.5 rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-purple-700"
                        :disabled="isSubmitting(card.id)"
                        :class="{ 'cursor-not-allowed opacity-60': isSubmitting(card.id) }"
                        @click="confirmStart(card)"
                    >
                        <Icon name="heroicons:check-circle" class="h-4 w-4" />
                        {{ isSubmitting(card.id) ? 'Ukladám...' : 'Potvrď začiatok' }}
                    </button>
                    <button
                        type="button"
                        class="text-center text-xs font-medium text-gray-600 dark:text-txt-body hover:text-gray-900 hover:dark:text-txt-headings hover:underline sm:text-right"
                        :disabled="isSubmitting(card.id)"
                        :class="{ 'cursor-not-allowed opacity-60': isSubmitting(card.id) }"
                        @click="handleSecondaryAction(card)"
                    >
                        {{ card.secondaryLabel }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { StatusChangedBy, type Mentoring } from '#shared/types/Mentoring'
import { RequestStatus } from '#shared/types/ParticipationRequest'
import { useChat } from '@/composables/chat/useChat'
import { useTopMidpointEvaluation } from '@/composables/mentoring/useCheckpoints'
import { useMentoring } from '@/composables/mentoring/useMentoring'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import RejectPreStartMentoringModal from '@/components/mentoring/modals/RejectPreStartMentoringModal.vue'
import { useModalStore } from '@/stores/modal'

const props = defineProps<{
    mentorings: Mentoring[]
    userId: string | null | undefined
}>()

const emit = defineEmits<{
    (e: 'resolved', payload: { id: string; action: 'start' | 'reject' | 'archive' }): void
}>()

type ActionCard = {
    id: string
    mentoring: Mentoring
    title: string
    subtitle: string
    partnerInitial: string
    partnerName: string
    userIsMentor: boolean
    didCurrentUserInitiate: boolean
    secondaryLabel: string
    to: string
}

const modalStore = useModalStore()
const { acceptMentoring, archiveMentoringForUser, rejectMentoring, resolveMentoringRequestChat, updateMentoring } = useMentoring()
const { sendMessage } = useChat()
const { createEvaluation } = useTopMidpointEvaluation()

const resolvedIds = ref<string[]>([])
const submittingIds = ref<string[]>([])

const initialOf = (firstName?: string, lastName?: string) => {
    const f = firstName?.trim()?.[0]
    const l = lastName?.trim()?.[0]
    return ((f ?? '') + (l ?? '')).toUpperCase() || '?'
}

const setSubmitting = (id: string, submitting: boolean) => {
    submittingIds.value = submitting
        ? [...new Set([...submittingIds.value, id])]
        : submittingIds.value.filter((submittingId) => submittingId !== id)
}

const markResolved = (id: string, action: 'start' | 'reject' | 'archive') => {
    resolvedIds.value = [...new Set([...resolvedIds.value, id])]
    emit('resolved', { id, action })
}

const isSubmitting = (id: string) => submittingIds.value.includes(id)

const cards = computed<ActionCard[]>(() => {
    if (!props.userId) return []
    return props.mentorings
        .filter((m) => Boolean(m.id))
        .filter((m) => !resolvedIds.value.includes(m.id!))
        .map((m) => {
            const userIsMentor = m.mentorId === props.userId
            const partnerMeta = userIsMentor ? m.menteeMeta : m.mentorMeta
            const partnerName = `${partnerMeta?.firstName ?? ''} ${partnerMeta?.lastName ?? ''}`.trim() || 'tvoj partner'
            const didCurrentUserInitiate = m.initiatedByMentor === true
                ? m.mentorId === props.userId
                : m.menteeId === props.userId
            const meConfirmed = userIsMentor ? m.startConfirmedByMentor : m.startConfirmedByMentee
            const themConfirmed = userIsMentor ? m.startConfirmedByMentee : m.startConfirmedByMentor
            // Cards we render: Accepted (nobody confirmed) OR partner-confirmed only
            const partnerConfirmedOnly = themConfirmed && !meConfirmed
            // Neutral framing — don't assume the user wants to accept. Subtitle hints at both paths.
            const title = partnerConfirmedOnly
                ? `${partnerName} potvrdil/a začiatok mentoringu`
                : `Žiadosť o mentoring s ${partnerName}`
            const subtitle = partnerConfirmedOnly
                ? 'Ak máte záujem pokračovať, potvrď začiatok. Inak ho môžeš odmietnuť.'
                : 'Rozhodni sa, či mentoring potvrdíš alebo odmietneš.'
            return {
                id: m.id!,
                mentoring: m,
                title,
                subtitle,
                partnerInitial: initialOf(partnerMeta?.firstName, partnerMeta?.lastName),
                partnerName,
                userIsMentor,
                didCurrentUserInitiate,
                secondaryLabel: didCurrentUserInitiate
                    ? 'Archivovať'
                    : userIsMentor
                        ? 'Odmietnuť žiadosť'
                        : 'Odmietnuť ponuku',
                to: `/profile/activity/mentoring/${m.id}`
            }
        })
})

async function confirmStart(card: ActionCard) {
    if (!props.userId || isSubmitting(card.id)) return

    const confirmed = await modalStore.openModal(ConfirmModal, {
        title: 'Potvrdiť začiatok mentoringu',
        message: `Potvrdením označíš mentoring s ${card.partnerName} ako spustený.`,
        confirmText: 'Potvrdiť začiatok',
        cancelText: 'Zrušiť',
        confirmVariant: 'primary'
    })

    if (!confirmed) return

    setSubmitting(card.id, true)
    try {
        const who = card.userIsMentor ? StatusChangedBy.Mentor : StatusChangedBy.Mentee

        if (card.mentoring.status !== RequestStatus.Accepted) {
            await acceptMentoring(card.id, who)
        }

        await updateMentoring(card.id, {
            startConfirmedByMentor: true,
            startConfirmedByMentee: true,
            startedAt: new Date() as any,
            startedBy: props.userId
        })

        createEvaluation(card.id, new Date()).catch((err) =>
            console.error('Error creating midpoint evaluation:', err)
        )

        markResolved(card.id, 'start')
    } catch (error) {
        console.error('Error confirming mentoring start from list:', error)
    } finally {
        setSubmitting(card.id, false)
    }
}

async function handleSecondaryAction(card: ActionCard) {
    if (!props.userId || isSubmitting(card.id)) return

    if (card.didCurrentUserInitiate) {
        await archiveMentoring(card)
        return
    }

    await rejectMentoringFromList(card)
}

async function archiveMentoring(card: ActionCard) {
    const confirmed = await modalStore.openModal(ConfirmModal, {
        title: 'Archivovať žiadosť',
        message: 'Skryjeme ju z tvojho zoznamu. Druhej strane nepošleme správu a mentoring neoznačíme ako odmietnutý.',
        confirmText: 'Archivovať',
        cancelText: 'Zrušiť',
        confirmVariant: 'decline'
    })

    if (!confirmed || !props.userId) return

    setSubmitting(card.id, true)
    try {
        await archiveMentoringForUser(card.id, props.userId)
        markResolved(card.id, 'archive')
    } catch (error) {
        console.error('Error archiving mentoring from list:', error)
    } finally {
        setSubmitting(card.id, false)
    }
}

async function rejectMentoringFromList(card: ActionCard) {
    const result = (await modalStore.openModal(RejectPreStartMentoringModal, {
        isMentor: card.userIsMentor,
        partnerName: card.partnerName
    })) as { messageForMentee?: string } | null

    if (result === null) return

    const reason = result.messageForMentee?.trim()

    setSubmitting(card.id, true)
    try {
        const who = card.userIsMentor ? StatusChangedBy.Mentor : StatusChangedBy.Mentee
        await rejectMentoring(card.id, who, card.userIsMentor && reason ? { messageForMentee: reason } : undefined)

        if (reason) {
            const chat = await resolveMentoringRequestChat(card.mentoring)
            if (chat?.id) {
                await sendMessage(chat.id, reason)
            }
        }

        markResolved(card.id, 'reject')
    } catch (error) {
        console.error('Error rejecting mentoring from list:', error)
    } finally {
        setSubmitting(card.id, false)
    }
}
</script>
